import type { CSSProperties } from "react";
import heroGymBg from "@/assets/hero-gym-bg.jpg";
// Image intégrée au bundle (et non un pointeur /__l5e) : les domaines
// personnalisés et les apps natives ne servent pas le proxy d'assets Lovable.
import scheduleBackground from "@/assets/vb20-schedule-background.jpg";

export type AthleteThemeVariant = "standard" | "dark-premium";
export type ScheduleDensity = "comfortable" | "compact";
export type AthleteBackgroundKey = "dashboard" | "schedule" | "reservations" | "stats" | "subscriptions" | "profile";

export interface AthleteThemePalette {
  primary: string;
  background: string;
  surface: string;
  foreground: string;
  muted: string;
  border: string;
}

export interface AthleteSchedulePalette {
  available: string;
  limited: string;
  nearlyFull: string;
  full: string;
  booked: string;
}

export type AthleteBackgrounds = Partial<Record<AthleteBackgroundKey, string | null>>;

export interface AthleteThemeSettings {
  version: 1;
  variant: AthleteThemeVariant;
  palette: AthleteThemePalette;
  schedulePalette: AthleteSchedulePalette;
  scheduleDensity: ScheduleDensity;
  backgrounds: AthleteBackgrounds;
}

export type AthleteThemeConfig = Partial<Omit<AthleteThemeSettings, "palette" | "schedulePalette" | "backgrounds">> & {
  palette?: Partial<AthleteThemePalette>;
  schedulePalette?: Partial<AthleteSchedulePalette>;
  backgrounds?: AthleteBackgrounds;
};

export const STANDARD_ATHLETE_THEME: AthleteThemeSettings = {
  version: 1,
  variant: "standard",
  palette: {
    primary: "#F59E0B",
    background: "#FFFFFF",
    surface: "#FFFFFF",
    foreground: "#111827",
    muted: "#6B7280",
    border: "#E5E7EB",
  },
  schedulePalette: {
    available: "#2E9D59",
    limited: "#EAB308",
    nearlyFull: "#EA7A15",
    full: "#C2413B",
    booked: "#168A4A",
  },
  scheduleDensity: "comfortable",
  backgrounds: {},
};

export const VB20_ATHLETE_THEME: AthleteThemeSettings = {
  version: 1,
  variant: "dark-premium",
  palette: {
    primary: "#F2B93F",
    background: "#080808",
    surface: "#111111",
    foreground: "#F7F7F5",
    muted: "#B9BBC1",
    border: "#353535",
  },
  schedulePalette: {
    available: "#2E9D59",
    limited: "#EAB308",
    nearlyFull: "#EA7A15",
    full: "#737373",
    booked: "#168A4A",
  },
  scheduleDensity: "compact",
  backgrounds: {
    dashboard: heroGymBg,
    schedule: scheduleBackground,
    reservations: heroGymBg,
    stats: heroGymBg,
    subscriptions: heroGymBg,
    profile: heroGymBg,
  },
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function hexToHsl(hex: string): string {
  if (!HEX_COLOR.test(hex)) throw new Error("Invalid hexadecimal color");
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let saturation = 0;
  const lightness = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === r) hue = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    if (max === g) hue = ((b - r) / delta + 2) / 6;
    if (max === b) hue = ((r - g) / delta + 4) / 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

const colorOr = (value: unknown, fallback: string) =>
  typeof value === "string" && HEX_COLOR.test(value) ? value : fallback;

export function resolveAthleteTheme(
  configured: AthleteThemeConfig | undefined,
  isVb20: boolean,
  legacyPrimary?: string,
): AthleteThemeSettings {
  const fallback = isVb20 ? VB20_ATHLETE_THEME : STANDARD_ATHLETE_THEME;
  const palette = configured?.palette;
  const schedulePalette = configured?.schedulePalette;
  return {
    version: 1,
    variant: configured?.variant === "dark-premium" || configured?.variant === "standard"
      ? configured.variant
      : fallback.variant,
    palette: {
      primary: colorOr(palette?.primary ?? legacyPrimary, fallback.palette.primary),
      background: colorOr(palette?.background, fallback.palette.background),
      surface: colorOr(palette?.surface, fallback.palette.surface),
      foreground: colorOr(palette?.foreground, fallback.palette.foreground),
      muted: colorOr(palette?.muted, fallback.palette.muted),
      border: colorOr(palette?.border, fallback.palette.border),
    },
    schedulePalette: {
      available: colorOr(schedulePalette?.available, fallback.schedulePalette.available),
      limited: colorOr(schedulePalette?.limited, fallback.schedulePalette.limited),
      nearlyFull: colorOr(schedulePalette?.nearlyFull, fallback.schedulePalette.nearlyFull),
      full: colorOr(schedulePalette?.full, fallback.schedulePalette.full),
      booked: colorOr(schedulePalette?.booked, fallback.schedulePalette.booked),
    },
    scheduleDensity: configured?.scheduleDensity === "compact" || configured?.scheduleDensity === "comfortable"
      ? configured.scheduleDensity
      : fallback.scheduleDensity,
    backgrounds: { ...fallback.backgrounds, ...(configured?.backgrounds ?? {}) },
  };
}

export type AthleteThemeStyle = CSSProperties & Record<`--${string}`, string>;

export function athleteThemeStyle(theme: AthleteThemeSettings): AthleteThemeStyle {
  return {
    "--primary": hexToHsl(theme.palette.primary),
    "--accent": hexToHsl(theme.palette.primary),
    "--ring": hexToHsl(theme.palette.primary),
    "--background": hexToHsl(theme.palette.background),
    "--card": hexToHsl(theme.palette.surface),
    "--popover": hexToHsl(theme.palette.surface),
    "--foreground": hexToHsl(theme.palette.foreground),
    "--card-foreground": hexToHsl(theme.palette.foreground),
    "--popover-foreground": hexToHsl(theme.palette.foreground),
    "--muted-foreground": hexToHsl(theme.palette.muted),
    "--border": hexToHsl(theme.palette.border),
    "--schedule-available": hexToHsl(theme.schedulePalette.available),
    "--schedule-limited": hexToHsl(theme.schedulePalette.limited),
    "--schedule-nearly-full": hexToHsl(theme.schedulePalette.nearlyFull),
    "--schedule-full": hexToHsl(theme.schedulePalette.full),
    "--schedule-booked": hexToHsl(theme.schedulePalette.booked),
  };
}

export function mergeAthleteThemeConfig(
  current: AthleteThemeConfig | undefined,
  next: AthleteThemeConfig,
): AthleteThemeConfig {
  return {
    ...current,
    ...next,
    palette: { ...current?.palette, ...next.palette },
    schedulePalette: { ...current?.schedulePalette, ...next.schedulePalette },
    backgrounds: next.backgrounds !== undefined
      ? { ...next.backgrounds }
      : { ...current?.backgrounds },
  };
}